import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useMemo } from 'react';

import type { PostOfficeContextValue } from '@atlassian/post-office-context/types';

import { usePostOfficeContextScope as useContextScope } from '@atlassian/post-office-context/post-office-context-scope';

import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';

import {
	usePlacementGraphQLSubscriptionApi,
	getSubscriptionMessages,
} from '@post-office/use-placement-api/graphql-subscription';

import { usePostOfficeSearchParams } from '@post-office/use-post-office-search-params';

import { scopes } from './scopes';

/**
 * GraphQL data fragment requested for the `jsm-live-chat` message template
 * delivered via this placement. The naming convention is
 * `PostOfficeMessage<PlacementId><MessageTemplateId>Data` (PascalCase) and is
 * generated server-side once the message template is registered.
 *
 * Add additional fragments here as new message templates are added that should
 * be delivered through this placement.
 */
const JSM_LIVE_CHAT_DATA_FRAGMENT = `... on PostOfficeMessageJsmLiveChatNotificationsJsmLiveChatData {
	__typename
	title
	description
	projectKey
	tenantUrl
	conversationId
}`;

type PostOfficeMessageJsmLiveChatNotificationsJsmLiveChatData = {
	__typename: 'PostOfficeMessageJsmLiveChatNotificationsJsmLiveChatData';
	title: string;
	description: string;
	projectKey?: string;
	tenantUrl?: string;
	conversationId?: string;
};

type SubscriptionMessageData = PostOfficeMessageJsmLiveChatNotificationsJsmLiveChatData;

type JsmLiveChatSubscriptionVariables = {
	activeScopes: string;
	context: Pick<PostOfficeContextValue, 'subproduct'>;
};

const doesScopeMatchPathname = (scopeName: string, pathname?: string): boolean => {
	if (!pathname) {
		return false;
	}

	const scope = scopes.find((scopeConfig) => scopeConfig.name === scopeName);
	const pathnameMatcher = scope?.when?.route?.pathname as
		| string
		| { equals?: string; matches?: string }
		| undefined;

	if (!pathnameMatcher) {
		return true;
	}
	if (typeof pathnameMatcher === 'string') {
		return pathname === pathnameMatcher;
	}
	if (typeof pathnameMatcher.equals === 'string') {
		return pathname === pathnameMatcher.equals;
	}
	if (typeof pathnameMatcher.matches === 'string') {
		try {
			return new RegExp(pathnameMatcher.matches).test(pathname);
		} catch {
			return false;
		}
	}
	return true;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'jsm-live-chat-notifications',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
		hideInIsolatedCloud: false,
		subscription: {
			enabled: true,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

const JsmLiveChatSubscriptionContent = ({
	Messages,
	variables,
}: {
	Messages: { render: AnyFrontendStage };
	variables: JsmLiveChatSubscriptionVariables;
}) => {
	const { data: subscriptionData, error: subscriptionError } =
		usePlacementGraphQLSubscriptionApi<SubscriptionMessageData>({
			dataFragments: [JSM_LIVE_CHAT_DATA_FRAGMENT],
			variables,
		});

	const messages = useMemo(
		() => getSubscriptionMessages<SubscriptionMessageData>(subscriptionData),
		[subscriptionData],
	);

	if (subscriptionError) {
		return null;
	}

	if (messages.length === 0) {
		return null;
	}

	// Render all live messages — each rendered template instance owns its own
	// browser-notification side effect (dispatched once on mount). React keys
	// off `messageInstanceId` ensure a fresh component (and therefore a fresh
	// notification) per incoming event.
	return (
		<>
			{messages.map(({ data, ...message }) => {
				if (!data) {
					return null;
				}
				return <Messages.render key={message.messageInstanceId} {...message} {...data} />;
			})}
		</>
	);
};

/**
 * jsm-live-chat-notifications is an invisible "headless" placement: it does not
 * render any UI of its own. It opens a GraphQL live subscription and forwards
 * every incoming message to the registered message template's render component.
 *
 * The message template (`jsm-live-chat`) is responsible for the visible side
 * effect — requesting `Notification` permission and dispatching a browser
 * notification on each new event. On notification click, the user is taken to
 * the JSM live chat URL built from the `tenantUrl` and `projectKey` fields
 * carried in the message context.
 */
export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const activeScopes = useContextScope({
		scopes,
		treatIntersectionAsSame: true,
	});
	const activeScopeName = activeScopes.current[0]?.name;
	const { pathname } = usePostOfficeSearchParams();
	const { productEdition, subproduct } = usePostOfficeContext();
	const PREMIUM_EDITION = 'PREMIUM_EDITION';

	const isScopePathnameAligned = useMemo(
		() => (activeScopeName ? doesScopeMatchPathname(activeScopeName, pathname) : false),
		[activeScopeName, pathname],
	);

	const variables = useMemo<JsmLiveChatSubscriptionVariables | null>(
		() =>
			activeScopeName && isScopePathnameAligned
				? {
						activeScopes: activeScopeName,
						context: {
							subproduct,
						},
					}
				: null,
		[activeScopeName, isScopePathnameAligned, subproduct],
	);

	if (!variables || productEdition !== PREMIUM_EDITION) {
		return null;
	}

	return <JsmLiveChatSubscriptionContent Messages={Messages} variables={variables} />;
});
